import { z } from 'zod';
import { ErrorResponseSchema } from '@/schemas/common/base';
import { idValidator } from '@/schemas/common/validators';

/**
 * GET /api/listings/[id] param schema
 * Validates the listing ID from the URL query parameter
 */
export const GetListingParamSchema = z.object({
  id: z.string().transform(val => parseInt(val, 10)).pipe(z.number().int().positive()),
});

export type GetListingParam = z.infer<typeof GetListingParamSchema>;

/**
 * Listing data schema for GET /api/listings/[id]
 * Uses z.any() for the listing object due to complex and varying structure
 * from Elasticsearch and database sources
 */
export const GetListingDataSchema = z.any();

/**
 * GET /api/listings/[id] response schema
 * Returns full listing details including user, photos, and tags
 */
export const GetListingResponseSchema = z.discriminatedUnion('success', [
  z.object({
    success: z.literal(true),
    listing: GetListingDataSchema,
  }),
  ErrorResponseSchema,
]);

export type GetListingData = z.infer<typeof GetListingDataSchema>;
export type GetListingResponse = z.infer<typeof GetListingResponseSchema>;

// =============================================================================
// Integration Test Schemas
// More permissive schemas used in integration/smoke tests to accommodate
// varying API response structures from different environments
// =============================================================================

/**
 * Integration test success schema for GET /api/listings/[id]
 * More permissive than production schema to handle legacy API variations
 */
export const IntegrationGetListingSuccessSchema = z.object({
  success: z.literal(true),
  listing: z.object({
    id: z.union([z.string(), z.number()]),
    title: z.string(),
    text: z.string().nullish(),
    price: z.union([z.number(), z.string()]),
    categoryId: z.number(),
    categoryGroupId: z.number().optional(),
    status: z.number(),
    shippable: z.union([z.number(), z.boolean()]).optional(),
    hasPhoto: z.number().optional(),
    photos: z.array(z.any()).optional(),
    user: z.object({
      id: z.union([z.string(), z.number()]),
      firstName: z.string(),
      lastName: z.string(),
    }).passthrough().optional(),
  }).passthrough(),
});

export type IntegrationGetListingSuccess = z.infer<typeof IntegrationGetListingSuccessSchema>;

/**
 * Integration test error schema for GET /api/listings/[id]
 */
export const IntegrationGetListingErrorSchema = z.object({
  success: z.literal(false),
  error: z.string().optional(),
  message: z.string().optional(),
});

export type IntegrationGetListingError = z.infer<typeof IntegrationGetListingErrorSchema>;

/**
 * Combined integration test response schema
 */
export const IntegrationGetListingResponseSchema = z.union([
  IntegrationGetListingSuccessSchema,
  IntegrationGetListingErrorSchema,
]);

/**
 * The automatic buyer coupon GET /api/listings/[id] attaches to a listing.
 *
 * The route writes these as flat keys beside the listing (see
 * pages/api/listings/[id].ts): `price` is already the sale price, and
 * `couponDiscountAmount` is what came off it. Only auto-apply coupons are ever
 * attached here; a code the buyer types never reaches this shape.
 *
 * Every field is coerced because the listing object is `z.any()` above and
 * crosses the network as JSON. A listing without an auto coupon fails this
 * parse, which is how a reader tells the two apart.
 */
export const ListingAutoCouponFieldsSchema = z.object({
  price: z.coerce.number().finite().nonnegative(),
  couponId: z.coerce.number().int().positive(),
  couponName: z.string().nullish(),
  couponDiscountAmount: z.coerce.number().finite().positive(),
  couponDiscountType: z.string().nullish(),
  couponDiscountValue: z.coerce.number().finite().nullish(),
  percentOff: z.coerce.number().finite().nullish(),
  slashedPrice: z.string().nullish(),
});

export type ListingAutoCouponFields = z.infer<typeof ListingAutoCouponFieldsSchema>;
